/** One page of the document being built: which page of which open file, and what to do to it. */
export interface PagePlan {
  /** The id `PdfEngine.open` returned for the file. */
  readonly source: number;
  /** Zero-based page index in that file. */
  readonly page: number;
  /** Extra clockwise rotation, added to the page's own. */
  readonly rotate?: 0 | 90 | 180 | 270;
  readonly images?: readonly PlacedImage[];
}

/**
 * An image drawn on a page, in PDF points (1/72 inch) in the page's own coordinate space: origin
 * at the bottom-left of the unrotated page, as the PDF stores it. The screens convert from what
 * the user sees.
 */
export interface PlacedImage {
  readonly bytes: ArrayBuffer;
  readonly type: 'png' | 'jpeg';
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  /**
   * Clockwise degrees about the image's centre, as seen on the unrotated page (decision 99: the
   * placed image can be rotated). `x`/`y`/`width`/`height` describe the box before rotation.
   */
  readonly rotation?: number;
}

export interface PageSize {
  /** The visible area (the CropBox, as pdf.js draws it): its origin and size, in points. */
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  /** The page's own rotation in degrees (0, 90, 180, 270). */
  readonly rotation: number;
}

export interface PdfInfo {
  readonly pages: number;
  readonly sizes: readonly PageSize[];
  /** Carries a digital signature: editing the file makes it invalid, so the screens warn first. */
  readonly signed: boolean;
  /** Was protected by an owner password only (opened with an empty one); the output is not. */
  readonly wasEncrypted: boolean;
}

export type PdfErrorCode = 'password-required' | 'not-a-pdf' | 'failed' | 'closed';

/** Internal: the Worker cannot load the PDF library; the engine moves to the page. */
export const ENGINE_UNAVAILABLE = 'engine-unavailable';

export class PdfEngineError extends Error {
  constructor(
    readonly code: PdfErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'PdfEngineError';
  }
}
